import React, { useState, useEffect, useCallback } from 'react';
import { API } from '../../App';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { CheckCircle2, XCircle, Clock, QrCode, Upload, Loader2, RefreshCw, Undo2, History } from 'lucide-react';
import { toast } from 'sonner';

const PaymentVerifications = () => {
  const BACKEND = API.replace(/\/api$/, '');
  const [pending, setPending] = useState([]);
  const [refunds, setRefunds] = useState([]);
  const [refundHistory, setRefundHistory] = useState({ refunds: [], total_refunded: 0, count: 0 });
  const [showHistory, setShowHistory] = useState(false);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectNote, setRejectNote] = useState('');
  const [qrUrl, setQrUrl] = useState('');
  const [qrFile, setQrFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    try {
      const [pendRes, refundRes, historyRes, setRes] = await Promise.all([
        axios.get(`${API}/bookings/pending-verifications`),
        axios.get(`${API}/bookings/pending-refunds`),
        axios.get(`${API}/bookings/refunds/history`),
        axios.get(`${API}/settings`),
      ]);
      setPending(pendRes.data || []);
      setRefunds(refundRes.data || []);
      setRefundHistory(historyRes.data || { refunds: [], total_refunded: 0, count: 0 });
      setQrUrl(setRes.data?.upi_qr_code_url || '');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load verifications');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const confirmRefund = async (booking) => {
    setActing(booking.id);
    try {
      await axios.post(`${API}/bookings/${booking.id}/confirm-refund`);
      toast.success('Refund confirmed — student, executive and admin notified.');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not confirm refund');
    } finally {
      setActing(null);
    }
  };

  const approve = async (booking) => {
    setActing(booking.id);
    try {
      await axios.post(`${API}/bookings/${booking.id}/verify-payment`, { action: 'approve' });
      toast.success('Payment approved — booking confirmed & student notified.');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not approve payment');
    } finally {
      setActing(null);
    }
  };

  const confirmReject = async () => {
    if (!rejectTarget) return;
    setActing(rejectTarget.id);
    try {
      await axios.post(`${API}/bookings/${rejectTarget.id}/verify-payment`, {
        action: 'reject',
        note: rejectNote || null,
      });
      toast.success('Payment rejected — student notified.');
      setRejectTarget(null);
      setRejectNote('');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not reject payment');
    } finally {
      setActing(null);
    }
  };

  const handleQrUpload = async () => {
    if (!qrFile) {
      toast.error('Please choose an image first');
      return;
    }
    const formData = new FormData();
    formData.append('file', qrFile);
    setUploading(true);
    try {
      const res = await axios.post(`${API}/settings/upi-qr-upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setQrUrl(res.data?.upi_qr_code_url || res.data?.url || '');
      setQrFile(null);
      toast.success('UPI QR code updated');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'QR upload failed');
    } finally {
      setUploading(false);
    }
  };

  const qrSrc = qrUrl ? (qrUrl.startsWith('http') ? qrUrl : `${BACKEND}${qrUrl}`) : '';

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-testid="payment-verifications-page">
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Payment Verifications</h1>
          <p className="text-slate-600">Verify student payments and confirm bookings</p>
        </div>
        <Button variant="outline" onClick={load} className="rounded-full" data-testid="refresh-verifications-button">
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Pending verifications */}
        <div className="lg:col-span-2 space-y-4">
          <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <Clock className="w-5 h-5 text-amber-600" />
            Awaiting Verification
            <Badge className="bg-amber-100 text-amber-700 border-amber-300" data-testid="pending-count-badge">
              {pending.length}
            </Badge>
          </h2>

          {loading ? (
            <div className="py-16 text-center text-slate-500">Loading...</div>
          ) : pending.length === 0 ? (
            <Card className="border-0 shadow">
              <CardContent className="py-16 text-center" data-testid="no-pending-verifications">
                <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
                <p className="text-slate-600">No payments awaiting verification. You're all caught up!</p>
              </CardContent>
            </Card>
          ) : (
            pending.map((b) => (
              <Card key={b.id} className="border-0 shadow-lg" data-testid={`verification-card-${b.id}`}>
                <CardContent className="p-5">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-slate-900" data-testid={`verification-student-${b.id}`}>{b.student_name}</span>
                        <span className="text-xs text-slate-400">{b.student_email}</span>
                      </div>
                      <p className="text-sm text-slate-600">
                        {b.subject} with <span className="font-medium">{b.teacher_name}</span>
                      </p>
                      <p className="text-sm text-slate-500">
                        {new Date(b.booking_date).toLocaleDateString()} • {b.start_time} • {b.duration_hours}h
                      </p>
                      <p className="text-lg font-bold text-violet-600">₹{Number(b.total_amount).toFixed(2)}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        onClick={() => approve(b)}
                        disabled={acting === b.id}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full"
                        data-testid={`approve-payment-${b.id}`}
                      >
                        {acting === b.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
                        Approve
                      </Button>
                      <Button
                        onClick={() => { setRejectTarget(b); setRejectNote(''); }}
                        disabled={acting === b.id}
                        variant="outline"
                        className="rounded-full border-red-300 text-red-600 hover:bg-red-50"
                        data-testid={`reject-payment-${b.id}`}
                      >
                        <XCircle className="w-4 h-4 mr-1" /> Reject
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}

          {/* Pending Refunds */}
          <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2 pt-4">
            <Undo2 className="w-5 h-5 text-rose-600" />
            Pending Refunds
            <Badge className="bg-rose-100 text-rose-700 border-rose-300" data-testid="refund-count-badge">
              {refunds.length}
            </Badge>
          </h2>

          {loading ? null : refunds.length === 0 ? (
            <Card className="border-0 shadow">
              <CardContent className="py-10 text-center" data-testid="no-pending-refunds">
                <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto mb-2" />
                <p className="text-slate-600">No refunds pending.</p>
              </CardContent>
            </Card>
          ) : (
            refunds.map((b) => (
              <Card key={b.id} className="border-0 shadow-lg border-l-4 border-l-rose-400" data-testid={`refund-card-${b.id}`}>
                <CardContent className="p-5">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-slate-900" data-testid={`refund-student-${b.id}`}>{b.student_name}</span>
                        <Badge className="bg-rose-100 text-rose-700 border-rose-300 text-xs">Cancelled</Badge>
                      </div>
                      <p className="text-sm text-slate-600">
                        {b.subject} with <span className="font-medium">{b.teacher_name}</span>
                        {b.executive_name && <> • by {b.executive_name}</>}
                      </p>
                      <p className="text-sm text-slate-500">
                        {new Date(b.booking_date).toLocaleDateString()} • {b.start_time} • {b.duration_hours}h
                      </p>
                      {b.cancel_reason && <p className="text-xs text-slate-500 italic">Reason: {b.cancel_reason}</p>}
                      <p className="text-lg font-bold text-rose-600">Refund ₹{Number(b.total_amount).toFixed(2)}</p>
                    </div>
                    <Button
                      onClick={() => confirmRefund(b)}
                      disabled={acting === b.id}
                      className="bg-rose-600 hover:bg-rose-700 text-white rounded-full"
                      data-testid={`confirm-refund-${b.id}`}
                    >
                      {acting === b.id ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Undo2 className="w-4 h-4 mr-1" />}
                      Confirm Refund
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))
          )}

          {/* Refund History (audit log) */}
          <div className="pt-4">
            <button
              onClick={() => setShowHistory((s) => !s)}
              className="w-full flex items-center justify-between text-lg font-semibold text-slate-900 hover:text-violet-700"
              data-testid="toggle-refund-history"
            >
              <span className="flex items-center gap-2">
                <History className="w-5 h-5 text-slate-500" />
                Refund History
                <Badge className="bg-slate-100 text-slate-600 border-slate-300" data-testid="refund-history-count">
                  {refundHistory.count}
                </Badge>
              </span>
              <span className="text-sm font-normal text-slate-500" data-testid="refund-history-total">
                Total refunded ₹{Number(refundHistory.total_refunded).toFixed(2)}
              </span>
            </button>

            {showHistory && (
              <div className="mt-3 space-y-2" data-testid="refund-history-list">
                {refundHistory.refunds.length === 0 ? (
                  <p className="text-sm text-slate-500 py-4 text-center">No refunds have been processed yet.</p>
                ) : (
                  refundHistory.refunds.map((b) => (
                    <div
                      key={b.id}
                      className="flex items-center justify-between bg-white border border-slate-200 rounded-xl px-4 py-3"
                      data-testid={`refund-history-item-${b.id}`}
                    >
                      <div>
                        <p className="text-sm font-medium text-slate-900">{b.student_name} • {b.subject}</p>
                        <p className="text-xs text-slate-500">
                          {b.teacher_name} • session {new Date(b.booking_date).toLocaleDateString()} {b.start_time}
                          {b.refunded_at && <> • refunded {new Date(b.refunded_at).toLocaleDateString()}</>}
                        </p>
                      </div>
                      <span className="text-sm font-bold text-emerald-600">₹{Number(b.total_amount).toFixed(2)}</span>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </div>
        <div>
          <Card className="border-0 shadow-lg sticky top-24" data-testid="qr-management-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <QrCode className="w-5 h-5 text-violet-600" /> UPI QR Code
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-slate-50 border rounded-xl p-4 text-center">
                {qrSrc ? (
                  <img src={qrSrc} alt="Current UPI QR" className="w-48 h-48 mx-auto object-contain" data-testid="current-qr-image" />
                ) : (
                  <p className="text-sm text-slate-500 py-12">No QR uploaded yet</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="qr-upload">Update QR image</Label>
                <Input
                  id="qr-upload"
                  type="file"
                  accept="image/*"
                  onChange={(e) => setQrFile(e.target.files?.[0] || null)}
                  data-testid="qr-file-input"
                />
                <Button
                  onClick={handleQrUpload}
                  disabled={uploading || !qrFile}
                  className="w-full bg-violet-600 hover:bg-violet-700 text-white rounded-full"
                  data-testid="upload-qr-button"
                >
                  {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                  {uploading ? 'Uploading...' : 'Upload New QR'}
                </Button>
                <p className="text-xs text-slate-500">Students scan this QR to pay. Update it whenever your UPI account changes.</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <Dialog open={!!rejectTarget} onOpenChange={(v) => { if (!v) setRejectTarget(null); }}>
        <DialogContent data-testid="reject-dialog">
          <DialogHeader>
            <DialogTitle>Reject payment</DialogTitle>
            <DialogDescription>
              Let {rejectTarget?.student_name} know why the payment couldn't be verified. This message is shown to them.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <Label htmlFor="reject-note">Reason (optional)</Label>
            <Textarea
              id="reject-note"
              placeholder="e.g. We couldn't find your payment. Please re-check and try again."
              value={rejectNote}
              onChange={(e) => setRejectNote(e.target.value)}
              rows={3}
              data-testid="reject-note-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectTarget(null)} className="rounded-full">Cancel</Button>
            <Button
              onClick={confirmReject}
              disabled={acting === rejectTarget?.id}
              className="bg-red-600 hover:bg-red-700 text-white rounded-full"
              data-testid="confirm-reject-button"
            >
              Reject Payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PaymentVerifications;
